import { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  api,
  formatApiError,
  getToken,
  setLocationScope,
  setToken,
} from "@/lib/api";

const AuthCtx = createContext(null);
const SCOPE_KEY = "vision.locationScope";

export const useAuth = () => {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
};

export const AuthProvider = ({ children }) => {
  // status: "loading" | "authed" | "anon"
  const [status, setStatus] = useState(getToken() ? "loading" : "anon");
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [selectedLocationId, setSelectedLocationIdState] = useState(null);

  /**
   * Snap the location scope to the caller's role:
   *  - Administrator: keep what's in localStorage (may be null = All).
   *  - Location Admin: scope is null (never send location_id param; server enforces user's location).
   *  - Everyone else: force to the user's own location_id.
   * Also mirrors into the axios interceptor so every GET auto-scopes.
   */
  const applyRoleScope = useCallback((u) => {
    if (!u) {
      setLocationScope(null);
      setSelectedLocationIdState(null);
      localStorage.removeItem(SCOPE_KEY);
      return;
    }
    if (u.role === "Administrator") {
      const stored = localStorage.getItem(SCOPE_KEY);
      setLocationScope(stored);
      setSelectedLocationIdState(stored);
    } else if (u.role === "Location Admin") {
      setLocationScope(null);
      setSelectedLocationIdState(null);
      localStorage.removeItem(SCOPE_KEY);
    } else {
      setLocationScope(u.location_id);
      setSelectedLocationIdState(u.location_id);
      // Non-admin: clear any lingering admin selection.
      localStorage.removeItem(SCOPE_KEY);
    }
  }, []);

  const bootstrap = useCallback(async () => {
    if (!getToken()) {
      setStatus("anon");
      setUser(null);
      applyRoleScope(null);
      return;
    }
    try {
      const { data } = await api.get("/auth/me", { _skipScope: true });
      setUser(data);
      applyRoleScope(data);
      setStatus("authed");
    } catch {
      setToken(null);
      setUser(null);
      applyRoleScope(null);
      setStatus("anon");
    }
  }, [applyRoleScope]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = useCallback(async (username, password) => {
    setError(null);
    try {
      const { data } = await api.post("/auth/login", { username, password });
      setToken(data.token);
      setUser(data.user);
      applyRoleScope(data.user);
      setStatus("authed");
      return { ok: true, user: data.user };
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      return { ok: false, error: msg };
    }
  }, [applyRoleScope]);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    applyRoleScope(null);
    setStatus("anon");
  }, [applyRoleScope]);

  /**
   * Only Administrators may change the scope.
   * `locId` = null → All Locations.
   */
  const setSelectedLocation = useCallback((locId) => {
    if (user?.role !== "Administrator") return;
    const val = locId || null;
    if (val) localStorage.setItem(SCOPE_KEY, val);
    else localStorage.removeItem(SCOPE_KEY);
    setLocationScope(val);
    setSelectedLocationIdState(val);
  }, [user]);

  const isGlobalAdmin = user?.role === "Administrator";
  const isAdmin = user?.role === "Administrator";
  const isLocationAdmin = user?.role === "Location Admin";
  const isSupervisor = user?.role === "Supervisor";
  const isAsstSupervisor = user?.role === "Assistant Supervisor";

  const value = {
    status,
    user,
    error,
    login,
    logout,
    refresh: bootstrap,
    isGlobalAdmin,
    isAdmin,
    isLocationAdmin,
    isSupervisor,
    isAsstSupervisor,
    selectedLocationId,
    setSelectedLocation,
  };

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
};
